import { motion } from "framer-motion";
import { ServicesCardProps } from "../utils/interfaces";
import { FiArrowRight } from "react-icons/fi";

const ServicesCard = ({
  title,
  content,
  iconLink,
  href,
}: ServicesCardProps) => {
  return (
    <motion.div
      className="group relative py-8 px-6 500:px-8 bg-gradient-to-br from-neutral-800/95 via-neutral-800 to-neutral-900/95 text-white rounded-3xl shadow-xl hover:shadow-2xl hover:shadow-primary-500/20 transition-all duration-500 overflow-hidden border border-neutral-700/50 backdrop-blur-sm hover:border-primary-500/50"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -12, scale: 1.02 }}
    >
      {/* Animated background gradient overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-primary-500/15 via-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        initial={false}
      />
      
      {/* Glow effect on hover */}
      <motion.div
        className="absolute inset-0 bg-primary-500/10 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500"
        initial={false}
      />

      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        {/* Icon Container */}
        <motion.div
          className="mb-6 relative"
          whileHover={{ scale: 1.1, rotate: [0, -5, 5, -5, 0] }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-primary-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-gradient-to-br from-primary-500/10 to-primary-600/5 rounded-2xl p-4 group-hover:from-primary-500/20 group-hover:to-primary-600/10 transition-all duration-300">
              <img
                src={iconLink}
                alt={title}
                width={80}
                height={80}
                className="object-contain filter brightness-0 invert group-hover:brightness-100 group-hover:invert-0 transition-all duration-300"
              />
            </div>
          </div>
        </motion.div>

        <h3 className="text-xl 500:text-2xl 700:text-3xl font-poppins-bold tracking-tight min-h-[70px] flex items-center text-neutral-100 group-hover:text-primary-400 transition-colors duration-300 mb-4">
          {title}
        </h3>

        <p className="font-poppins-regular line-clamp-5 text-neutral-300 leading-relaxed text-sm 500:text-base mb-6">
          {content}
        </p>

        <motion.a
          href={`/service#${href}`}
          target="_blank"
          className="mt-auto inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500/10 to-primary-600/5 hover:from-primary-500 hover:to-primary-600 rounded-xl text-primary-400 hover:text-white font-poppins-semibold text-sm 500:text-base transition-all duration-300 group/link border border-primary-500/30 hover:border-primary-500"
          whileHover={{ x: 5 }}
        >
          <span>Read More</span>
          <motion.div
            className="group-hover/link:translate-x-1 transition-transform duration-300"
            initial={false}
          >
            <FiArrowRight className="w-4 h-4 500:w-5 500:h-5" />
          </motion.div>
        </motion.a>
      </div>

      {/* Shine effect on hover */}
      <motion.div
        className="absolute inset-0 -left-full group-hover:left-full w-full h-full bg-gradient-to-r from-transparent via-primary-400/10 to-transparent transition-all duration-1000"
        initial={false}
      />
    </motion.div>
  );
};

export default ServicesCard;
